// pages/blog/[url_slug].jsx  // MERGED: serves BOTH posts and tag listings at /blog/:url_slug
import { Global, Collection } from "@sourceflow-uk/sourceflow-sdk";
import SeoHead from "../../components/SeoHead";
import { Helmet } from "react-helmet";
import LatestNews from "../../components/LatestNews";
import { Container } from "reactstrap";
import NewBlogDetails from "../../components/NewBlogDetails";
import NewSubBanner from "../../components/NewSubBanner";
import NewColumnWithForm from "../../components/NewColumnWithForm";
import NewBlogDetails2 from "../../components/NewBlogDetails2";

import AllBlogs from "../../components/AllBlogs";
import NewFeaturedBlogs from "../../components/NewFeaturedBlogs";
import NewColumnTextWithForm from "@/components/NewColumnTextWithForm";

// ---------- helpers ----------
function hasTag(item, tagName) {
  if (!item?.tag || !tagName) return false;
  const target = String(tagName).toLowerCase();
  const t = item.tag;
  if (Array.isArray(t)) return t.some((x) => String(x).toLowerCase() === target);
  if (Array.isArray(t?.en)) return t.en.some((x) => String(x).toLowerCase() === target);
  if (typeof t?.en === "string") return String(t.en).toLowerCase() === target;
  if (typeof t === "string") return t.toLowerCase() === target;
  return false;
}

// ---------- POST VIEW ----------
function PostView({ global, page, all_news, related_news }) {
  const breadcrumbList = [
    {
      "@type": "ListItem",
      name: "Carbon60",
      position: 1,
      item: { "@type": "Thing", "@id": `https://www.carbon60global.com/` },
    },
    {
      "@type": "ListItem",
      name: "Blogs",
      position: 2,
      item: { "@type": "Thing", "@id": `https://www.carbon60global.com/blog` },
    },
  ];

  return (
    <>
      <SeoHead
        page_name={`${page.title} | Carbon60${page.tag.includes("News & Insights") ? " Insights" : "" }`}
        description={page.tag.includes("Case Study") ? `Discover insights from "${page.title}" and how Carbon60 delivers effective solutions across Engineering.` : `Expert insights on ${page.title} from Carbon60, specialists in Engineering.`}
        canonical_link={`blog/${page.url_slug}`}
        og_image={page.image}
        og_img_alt={`${page.title}`}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbList,
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            description: page?.short_description || "",
            isFamilyFriendly: "http://schema.org/True",
            image: page?.image || "",
            dateModified: page?.updated_at || "",
            datePublished: page?.created_at || "",
            name: page?.title || "",
            headline: page?.title || "",
            articleBody: page?.content ? page.content.replace(/<[^>]*>/g, "") : "",
            publisher: {
              "@type": "Organization",
              name: "Carbon60",
              sameAs: "https://www.carbon60global.com/",
              logo: {
                "@type": "ImageObject",
                url: "https://cdn.sourceflow.co.uk/fy5o8mjc9xssbpgtw36m2aq695mv",
              },
            },
            author: { "@type": "Person", image: page?.author_image || "", name: page?.author || "" },
          })}
        </script>
      </Helmet>

      <NewSubBanner
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.main_banner`}
        customClass={``}
        content={<h1>{page.title}</h1>}
        imageURL1={`/site-assets/images/main-banner.jpg`}
        imageALT1={`Main Banner`}
        imageURL2={`/site-assets/images/banner.webp`}
        imageALT2={`Overlay Main Banner`}
        displayImage1={false}
        editableContent={false}
        buttonLink={`${
          page.tag.includes("Case Study")
            ? "/blog/case-studies"
            : page.tag.includes("Guides & Reports")
            ? "/blog/guides-and-reports"
            : "/blog"
        }`}
        buttonTitle={`${
          page.tag.includes("Case Study")
            ? "Back to Case Study"
            : page.tag.includes("Guides & Reports")
            ? "Back to Guides & Reports"
            : "Back to insights"
        }`}
        post={page}
        buttonLink2={`${page.tag.includes("Guides & Reports") ? "#download-form" : ""}`}
        buttonTitle2={`${page.tag.includes("Guides & Reports") ? "Download Report" : ""}`}
        ctaLink={page?.cta_link ?? null}
        ctaTitle={page?.cta_title ?? null}
      />

      {page.tag.includes("Guides & Reports") ? (
        <NewBlogDetails
          global={global}
          pathPrefix={`insights_page.guides_and_reports.new_blog_details`}
          page={page}
          related_news={related_news}
          page_prefix={`blog`}
          share_heading={`Share Report`}
          className={`py-5 guides-and-reports-page`}
          formId={`${page?.form_id ? page.form_id : "66e2baf1-9beb-48f1-b0f4-62f583b838d0"}`}
          locale={``}
        />
      ) : page.tag.includes("Case Study") ? (
        page.case_study_tabs?.length >= 1 && (
          <NewBlogDetails2 global={global} pathPrefix={`insights_page.new_blog_details_2`} page={page} />
        )
      ) : (
        <NewBlogDetails
          global={global}
          pathPrefix={`insights_page.new_blog_details`}
          page={page}
          related_news={related_news}
          page_prefix={`blog`}
          hubSpotForm={page.hubspot_form_id}
          locale={``}
        />
      )}

      {(page.tag.includes("Guides & Reports") || page.tag.includes("Case Study")) ? (
        <>
          <Container>
            <hr />
          </Container>
          <LatestNews
            global={global}
            pathPrefix={`${page.url_slug}.insights_page.guides_and_reports.latest_news`}
            customClass={`py-5 white-card`}
            post={all_news}
            content={<h2>{page.tag.includes("Guides & Reports") ? "Other Guides and Reports" : "Other Case Studies"}</h2>}
            buttonLink={``}
            buttonTitle={``}
            content_align={`left`}
            footer_buttonLink={`/blog${page.tag.includes("Guides & Reports") ? "/guides-and-reports" : "/case-study"}`}
            footer_buttonLink_title={`View All`}
          />
        </>
      ) : null}

      <NewColumnWithForm
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.new_column_with_form`}
        customClass={`pb-5`}
        heading={`How can we help you?`}
        formId={`6dfe644a-5f46-4e5d-9ba8-be0b6c0583c4`}
      />
    </>
  );
}

// ---------- TAG VIEW ----------
function TagView({ global, page, all_news }) {
  return (
    <>
      <SeoHead
        page_name={`${page.title} | Project engineering and technical recruitment specialists | Carbon60`}
        canonical_link={`blog/${page.url_slug}`}
      />
      <NewSubBanner
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.new_sub_banner`}
        customClass={``}
        content={<h1>{page.title}</h1>}
        imageURL1={`/site-assets/images/main-banner.jpg`}
        imageALT1={`${page.title}`}
        imageURL2={`/site-assets/images/banner.webp`}
        imageALT2={`Overlay Main Banner`}
        displayImage1={false}
        editableContent={false}
      />
      <NewFeaturedBlogs
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.new_featured_blogs`}
        customClass={`py-5 grayGradientBg`}
        content={<h2>Featured {page.title}</h2>}
        post={all_news}
        socialPagePrefix={`blog/${page.url_slug}`}
      />
      <AllBlogs
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.all_blogs`}
        customClass={`py-5 lightGreybg`}
        post={all_news}
        ParentNews={false}
        tagName={`${page.title}`}
        heading={`All ${page.title}`}
      />
      <Container>
        <hr />
      </Container>
      <NewColumnTextWithForm
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.new_column_text_with_form`}
        content={
          <>
            <h2>Drive better workforce performance</h2>
            <p>Book a call with us and find out how we can help you and your team achieve greater heights.</p>
          </>
        }
        formId={`6dfe644a-5f46-4e5d-9ba8-be0b6c0583c4`}
        customClass="py-5"
        locale=""
      />
    </>
  );
}

// ---------- UNIFIED EXPORT ----------
export default function BlogUnified(props) {
  return props.mode === "post" ? <PostView {...props} /> : <TagView {...props} />;
}

// ---------- DATA ----------
export async function getStaticProps({ params }) {
  const global = (await Global()).toJson();

  // 1) Try POST first
  let postPage = null;
  try {
    postPage = (await Collection("news")).getJsonItem(params.url_slug, "url_slug");
  } catch (_) {}

  if (postPage && postPage.url_slug) {
    const news = (await Collection("news"))
      .filter(
        (post) =>
          "tag" in post &&
          Array.isArray(post.tag?.en) &&
          post.tag.en.some((t) => "tag" in postPage && postPage.tag?.includes(t))
      );

    const all_news = news.orderBy("date").getItems().slice(0, 8);
    const related_news = news.orderBy("order").getItems().slice(0, 2);

    return {
      props: { global, mode: "post", page: postPage, all_news, related_news },
    };
  }

  // 2) Fallback to TAG
  let tagPage = null;
  try {
    tagPage = (await Collection("news_tag")).getJsonItem(params.url_slug, "url_slug");
  } catch (_) {}

  if (!tagPage) return { notFound: true };

  const tagName = tagPage.title;
  const news = (await Collection("news")).filter((post) => post.tag?.en?.includes(tagName));
  const all_news = news.orderBy((n) => Date.parse(n.date?.en), "desc").getItems();

  return {
    props: { global, mode: "tag", page: tagPage, all_news },
  };
}

export async function getStaticPaths() {
  const postPaths = (await Collection("news")).getPaths("url_slug");
  const tagPaths = (await Collection("news_tag")).getPaths("url_slug");

  return {
    paths: [...postPaths, ...tagPaths],
    fallback: false,
  };
}
